import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Logger, User, UserService} from '@nh/angular-tses';
import {RevisionService} from 'app/core/services/revision.service';
import {SnapshotService} from 'app/core/services/snapshot.service';
import {DAS_2_APPROVED_STATES, WorkflowStates} from 'app/workflow/workflow-states';
import {environment} from 'environments/environment';
import {combineLatest, Observable, of, Subject, Subscription} from 'rxjs';
import {BehaviorSubject} from 'rxjs/internal/BehaviorSubject';
import {map, tap} from 'rxjs/operators';
import {DiarySnapshot} from '../diary/diary-snapshot';
import {DiaryService} from '../diary/diary.service';
import {STATE_METADATA} from '../workflow/state-metadata';
import {getRolesHeld} from './departure-permissions';
import {RevisionState} from './revision-state';
import {assignmentRoleLookup, DasAction, DasRole, DepartureRole, GlobalAction, GlobalRole} from './types';

export type PermissionsLookup = Partial<Record<WorkflowStates, Partial<Record<DasAction, DasRole[]>>>>;

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private static readonly UNKNOWN_STATE = 'UNKNOWN_STATE';
  private static readonly DAS_2_APPROVED_STATE = 'DAS_2_APPROVED';
  private static readonly WILDCARD_STATE = '*';

  private readonly _actionMap: Map<DasAction, Observable<boolean>> = new Map();
  private readonly diarySnapshotSubject: BehaviorSubject<DiarySnapshot> = new BehaviorSubject(null);
  private readonly userSubject: BehaviorSubject<User> = new BehaviorSubject(null);
  private readonly globalRolesHeldSubject: BehaviorSubject<string[]> = new BehaviorSubject([]);
  private readonly isLatestRevisionSubject: BehaviorSubject<boolean> = new BehaviorSubject(null);
  private revisionsLookup: Record<DasAction, RevisionState> = null;
  private endpoint: string;
  private lookup: PermissionsLookup = null;

  // Completes once the necessary data is loaded
  private ready: Subject<void> = new Subject();

  constructor(
    revisionService: RevisionService,
    diaryService: DiaryService,
    userService: UserService,
    private http: HttpClient,
    private snapshotService: SnapshotService
  ) {
    diaryService.onDiarySnapshotChange().subscribe((d) => {
      this.diarySnapshotSubject.next(d);
    });

    revisionService.onLatestRevisionChange().subscribe((isLatest) => {
      this.isLatestRevisionSubject.next(isLatest);
    });
    userService.getUser().subscribe((u) => this.userSubject.next(u));
    userService.getRoles().subscribe((roles) => this.globalRolesHeldSubject.next(roles));

    this.endpoint = `${environment.tsesConfig.apiHost}/permissions`;

    const discoverReadiness: Subscription = combineLatest([
      this.userSubject,
      this.globalRolesHeldSubject,
      this.loadLookup(),
      this.loadRevisionPermissions(),
    ]).subscribe(([user, roles, loadedLookup, loadedRev]: [User, string[], any, any]) => {
      if (user && roles && roles.length && loadedLookup && loadedRev) {
        this.ready.complete();
        discoverReadiness.unsubscribe();
      }
    });
  }

  /**
   * Resolves once when the user, their roles, the permissions matrix, and the revision permissions info have all
   * loaded.  If these are already loaded, will resolve immediately.
   */
  public whenReady(): Promise<void> {
    return this.ready.toPromise();
  }

  /**
   * Should not be called before whenReady() has fired as this can occasionally return before role info
   * has been pulled down.
   */
  public isAdmin(): boolean {
    return this.globalRolesHeldSubject.value.includes(GlobalRole.DAS_ADMIN);
  }

  private loadLookup(): Observable<PermissionsLookup> {
    return this.http.get<PermissionsLookup>(this.endpoint).pipe(
      tap(
        (lookup: PermissionsLookup) => {
          this.lookup = lookup;
        },
        (err) => {
          Logger.error('permissions-error', 'Failed to load permissions matrix', err);
        }
      )
    );
  }

  private loadRevisionPermissions(): Observable<Record<DasAction, RevisionState>> {
    return this.http.get<Record<DasAction, any>>(`${this.endpoint}/revisions`).pipe(
      tap((record: Record<DasAction, any>) => {
        for (const key in record) {
          if (record.hasOwnProperty(key)) {
            record[key] = new RevisionState(record[key].name);
          }
        }

        this.revisionsLookup = record;
      })
    );
  }

  /**
   * Returns an Observable that broadcasts on changes in state to the action requested.
   *
   * @param action    {DasAction}              Action to take
   * @returns         {Observable<boolean>}    The Observable object
   */
  public can(action: DasAction): Observable<boolean> {
    if (this.snapshotService.viewingSnapshot()) {
      return of(false);
    }

    if (!this._actionMap.has(action)) {
      const actionObs = combineLatest([
        this.diarySnapshotSubject,
        this.userSubject,
        this.globalRolesHeldSubject,
        this.isLatestRevisionSubject,
      ]).pipe(
        map(([snapshot, user, globalRoles, isLatest]) => {
          return (
            this.canDoGlobalAction(action, globalRoles) || this.canDoDepartureAction(action, snapshot, user, isLatest)
          );
        })
      );

      this._actionMap.set(action, actionObs);
    }
    return this._actionMap.get(action);
  }

  /**
   * Returns whether the current user with their current permissions can do the action.
   *
   * @param action    {DasAction} Action to take
   * @returns         {boolean}   True iff the user can do the action.
   */
  public canNow(action: DasAction): boolean {
    if (this.snapshotService.viewingSnapshot()) {
      return false;
    }

    return this.canDoGlobalAction(action) || this.canDoDepartureAction(action);
  }

  private canDoGlobalAction(action: DasAction, globalRoles: string[] = this.globalRolesHeldSubject.value): boolean {
    if (Object.values(GlobalAction).includes(action as GlobalAction)) {
      return this._handleGlobalActions(action as GlobalAction, globalRoles);
    } else {
      return false;
    }
  }

  private canDoDepartureAction(
    action: DasAction,
    snapshot: DiarySnapshot = this.diarySnapshotSubject.value,
    user: User = this.userSubject.value,
    isLatest: boolean = this.isLatestRevisionSubject.value
  ): boolean {
    if (
      this.revisionsLookup &&
      this.revisionsLookup[action] &&
      !this.revisionsLookup[action].handleRevisionPermissions(isLatest)
    ) {
      return false;
    }
    const userId: string = user && user.id ? user.id.value : null;
    const rolesRequired: DasRole[] = this.getRolesRequired(action, snapshot);
    const rolesHeld: DasRole[] = snapshot ? getRolesHeld(snapshot.permissions, userId) : [];
    rolesHeld.push(...(this.globalRolesHeldSubject.value as DasRole[]));

    return this.hasRequiredRole(rolesRequired, rolesHeld);
  }

  private getRolesRequired(action: DasAction, snapshot: DiarySnapshot): DasRole[] {
    const workflowState: string = this.getWorkflowStateFromSnapshot(snapshot);

    const lookup: PermissionsLookup = this.lookup || {};
    const row: Partial<Record<DasAction, DasRole[]>> = lookup[workflowState] || {};
    const wildcardRow: Partial<Record<DasAction, DasRole[]>> = lookup[PermissionsService.WILDCARD_STATE] || {};

    return row[action] || wildcardRow[action] || [];
  }

  private hasRequiredRole(rolesRequired: DasRole[], rolesHeld: DasRole[]): boolean {
    if (!rolesRequired.length || !rolesHeld.length) {
      return false;
    }

    for (const held of rolesHeld) {
      if (rolesRequired.includes(held)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Sanitizes the workflow state name from the diary snapshot for null or non-approved values
   * @param snapshot Diary snapshot of current workflow
   */
  private getWorkflowStateFromSnapshot(snapshot: DiarySnapshot): string {
    if (!snapshot?.workflowState?.name) {
      return PermissionsService.UNKNOWN_STATE;
    }

    const workflowState: string = snapshot.workflowState.name;

    if (!!STATE_METADATA[workflowState]) {
      return workflowState;
    }

    return DAS_2_APPROVED_STATES.includes(workflowState)
      ? PermissionsService.DAS_2_APPROVED_STATE
      : PermissionsService.UNKNOWN_STATE;
  }

  /**
   * Handles the global action request.
   *
   * @param action {GlobalAction}          Global action user is looking to take
   */
  private _handleGlobalActions(action: GlobalAction, globalRoles: string[]): boolean {
    switch (action) {
      case GlobalAction.CREATE_DEPARTURE: {
        return globalRoles.includes(GlobalRole.DEPARTURE_CREATOR);
      }
      case GlobalAction.USER_MANAGEMENT: {
        return globalRoles.includes(GlobalRole.USER_MANAGEMENT);
      }
      case GlobalAction.VIEW_ADMIN_PAGE: {
        return globalRoles.includes(GlobalRole.VIEW_ADMIN_PAGE);
      }
      case GlobalAction.VIEW_ALL_ORGANISATIONS: {
        return globalRoles.includes(GlobalRole.VIEW_ALL_ORGANISATIONS);
      }
      case GlobalAction.VIEW_MY_ORGANISATIONS_FILTER: {
        return globalRoles.includes(GlobalRole.VIEW_MY_ORGANISATIONS_FILTER);
      }
      case GlobalAction.VIEW_MY_DISCIPLINES_FILTER: {
        return globalRoles.includes(GlobalRole.VIEW_MY_DISCIPLINES_FILTER);
      }
      case GlobalAction.VIEW_USER_LOCATIONS: {
        return globalRoles.includes(GlobalRole.VIEW_USER_LOCATIONS);
      }
      case GlobalAction.VIEW_RESTRICTED_SEARCH_COLUMNS: {
        return globalRoles.includes(GlobalRole.VIEW_RESTRICTED_SEARCH_COLUMNS);
      }
      case GlobalAction.VIEW_POST_DETERMINATION_FILTER: {
        return globalRoles.includes(GlobalRole.GLOBAL_PROJECT_MANAGER) || globalRoles.includes(GlobalRole.DAS_ADMIN);
      }
      default: {
        return false;
      }
    }
  }

  /**
   * Returns an observable of an array of departure roles which represent the roles which can
   * currently be reassigned by the user.
   */
  public getAssignableRoles(): Observable<DepartureRole[]> {
    if (this.snapshotService.viewingSnapshot()) {
      return of([]);
    }

    return combineLatest([this.diarySnapshotSubject, this.userSubject, this.globalRolesHeldSubject]).pipe(
      map(([snapshot, user, globalRoles]) => {
        return this._calculateAssignableRoles(snapshot, user, globalRoles);
      })
    );
  }

  private _calculateAssignableRoles(snapshot: DiarySnapshot, user: User, globalRoles: string[]): DepartureRole[] {
    if (!this.lookup) {
      return [];
    }

    const workflowState: string = this.getWorkflowStateFromSnapshot(snapshot);

    const row: Partial<Record<DasAction, DasRole[]>> = this.lookup[workflowState] || {};
    const wildcardRow: Partial<Record<DasAction, DasRole[]>> = this.lookup[PermissionsService.WILDCARD_STATE] || {};
    const actions: DasAction[] = [].concat(...Object.keys(row), ...Object.keys(wildcardRow));
    const assignableRoles: DepartureRole[] = [];

    actions.forEach((action: DasAction) => {
      if (this.canDoGlobalAction(action, globalRoles) || this.canDoDepartureAction(action, snapshot, user)) {
        const role: DepartureRole = assignmentRoleLookup[action];
        if (role) {
          assignableRoles.push(role);
        }
      }
    });

    return assignableRoles;
  }
}
